import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ragService from '../services/ragService';
import skillsService from '../services/skillsService';

export const ASSISTANT_MODES = {
  question: 'question',
  synthese: 'synthese',
};

function createMessageId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function sleep(ms) {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

export function getExampleQuestions(clientSlug) {
  const clientName = clientSlug
    ? clientSlug.split('-').map((part) => part.charAt(0).toUpperCase() + part.slice(1)).join(' ')
    : 'X';

  return [
    `Fais-moi le point sur le client ${clientName}`,
    `Que contient le contrat cadre de ${clientName} ?`,
    `Quels sujets reviennent dans les derniers RDV avec ${clientName} ?`,
  ];
}

function parseSlash(input) {
  if (!input.startsWith('/')) {
    return null;
  }
  const menuMatch = input.match(/^\/([a-z0-9-]*)$/i);
  if (menuMatch) {
    return { mode: 'menu', query: menuMatch[1].toLowerCase() };
  }
  const execMatch = input.match(/^\/([a-z0-9-]+)\s+([\s\S]*)$/i);
  if (execMatch) {
    return { mode: 'exec', slug: execMatch[1].toLowerCase(), rest: execMatch[2].trim() };
  }
  return null;
}

function firstTexteParam(skill) {
  return (skill.parametres || []).find((param) => param.type !== 'client' && param.requis);
}

function needsClient(skill) {
  return (skill.parametres || []).some((param) => param.type === 'client' && param.requis);
}

/**
 * Logique partagée page plein écran + widget flottant.
 */
export default function useAssistantChat({ initialClient = '' } = {}) {
  const [clients, setClients] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(true);
  const [clientsError, setClientsError] = useState('');
  const [selectedClient, setSelectedClient] = useState(initialClient || '');
  const [mode, setMode] = useState(ASSISTANT_MODES.question);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [pendingQuestion, setPendingQuestion] = useState(null);
  const [skills, setSkills] = useState([]);
  const [slashIndex, setSlashIndex] = useState(0);

  const textareaRef = useRef(null);
  const messagesEndRef = useRef(null);
  const selectedClientRef = useRef(selectedClient);
  const autoDetectedSlugRef = useRef(null);
  const userOverrideRef = useRef(false);
  const sendingRef = useRef(false);

  selectedClientRef.current = selectedClient;
  sendingRef.current = sending;

  const examples = getExampleQuestions(selectedClient);
  const syntheseDisabled = !selectedClient;

  const slashParsed = parseSlash(input);
  const slashItems = useMemo(() => {
    if (!slashParsed || slashParsed.mode !== 'menu') {
      return [];
    }
    const query = slashParsed.query;
    return skills.filter((skill) => {
      if (!query) {
        return true;
      }
      return skill.slug.includes(query)
        || skill.nom.toLowerCase().includes(query)
        || skill.description.toLowerCase().includes(query);
    });
  }, [skills, slashParsed]);

  const slashOpen = slashParsed?.mode === 'menu' && slashItems.length > 0;

  useEffect(() => {
    setSlashIndex(0);
  }, [input, slashItems.length]);

  useEffect(() => {
    let cancelled = false;

    const loadClients = async () => {
      setClientsLoading(true);
      setClientsError('');
      try {
        const data = await ragService.getClients();
        if (!cancelled) {
          setClients(data);
        }
      } catch (error) {
        if (!cancelled) {
          setClientsError(error.message || 'Impossible de charger la liste des clients');
        }
      } finally {
        if (!cancelled) {
          setClientsLoading(false);
        }
      }
    };

    loadClients();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    skillsService.list()
      .then((list) => {
        if (!cancelled) {
          setSkills(list);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setSkills([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (syntheseDisabled && mode === ASSISTANT_MODES.synthese) {
      setMode(ASSISTANT_MODES.question);
    }
  }, [syntheseDisabled, mode]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const resizeTextarea = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) {
      return;
    }
    textarea.style.height = 'auto';
    textarea.style.height = `${Math.min(textarea.scrollHeight, 160)}px`;
  }, []);

  useEffect(() => {
    resizeTextarea();
  }, [input, resizeTextarea]);

  const syncFromPageMatch = useCallback((matchedSlug) => {
    const next = matchedSlug || '';
    const current = selectedClientRef.current;
    const lastAuto = autoDetectedSlugRef.current || '';

    const stillOnAuto = !userOverrideRef.current || current === lastAuto;
    autoDetectedSlugRef.current = next || null;

    if (!stillOnAuto) {
      return;
    }

    userOverrideRef.current = false;
    if (current !== next) {
      setSelectedClient(next);
    }
  }, []);

  const handleClientChange = useCallback((slug) => {
    const next = slug || '';
    userOverrideRef.current = next !== (autoDetectedSlugRef.current || '');
    setSelectedClient(next);
  }, []);

  const pushAssistantError = useCallback((userContent, errorMessage) => {
    setMessages((prev) => [
      ...prev,
      { id: createMessageId(), role: 'user', content: userContent },
      { id: createMessageId(), role: 'assistant', error: errorMessage },
    ]);
  }, []);

  const pollExecution = useCallback(async (executionId, loadingId) => {
    const started = Date.now();
    while (Date.now() - started < 5 * 60 * 1000) {
      await sleep(skillsService.POLL_INTERVAL_MS);
      try {
        const execution = await skillsService.getExecution(executionId);
        if (execution.statut === 'termine') {
          setMessages((prev) => prev.map((message) => (
            message.id === loadingId
              ? { id: loadingId, role: 'assistant', content: execution.resultat || 'Terminé.' }
              : message
          )));
          return;
        }
        if (execution.statut === 'erreur') {
          setMessages((prev) => prev.map((message) => (
            message.id === loadingId
              ? { id: loadingId, role: 'assistant', error: execution.message || 'Le skill a échoué.' }
              : message
          )));
          return;
        }
      } catch {
        // nouvelle tentative au prochain tick
      }
    }
    setMessages((prev) => prev.map((message) => (
      message.id === loadingId
        ? { id: loadingId, role: 'assistant', content: 'Le skill tourne encore. Le résultat apparaîtra dans le journal d’administration.' }
        : message
    )));
  }, []);

  const runSkill = useCallback(async (skill, restText = '') => {
    if (sendingRef.current) {
      return;
    }

    const label = `/${skill.slug}${restText ? ` ${restText}` : ''}`;
    if (needsClient(skill) && !selectedClientRef.current) {
      pushAssistantError(label, 'Sélectionnez un client avant d’utiliser ce skill.');
      setInput('');
      return;
    }

    const texteParam = firstTexteParam(skill);
    if (texteParam && !restText) {
      setInput(`/${skill.slug} `);
      textareaRef.current?.focus();
      return;
    }

    const parametres = {};
    (skill.parametres || []).forEach((param) => {
      if (param.type === 'client') {
        parametres[param.nom] = selectedClientRef.current;
      } else if (texteParam && param.nom === texteParam.nom) {
        parametres[param.nom] = restText;
      }
    });

    const userMessage = { id: createMessageId(), role: 'user', content: label };
    const loadingId = createMessageId();
    setMessages((prev) => [
      ...prev,
      userMessage,
      { id: loadingId, role: 'assistant', loading: true },
    ]);
    setInput('');
    setSending(true);

    try {
      const execution = await skillsService.executer(skill.slug, parametres);
      if (execution.statut === 'termine') {
        setMessages((prev) => prev.map((message) => (
          message.id === loadingId
            ? { id: loadingId, role: 'assistant', content: execution.resultat || 'Terminé.' }
            : message
        )));
      } else if (execution.statut === 'erreur') {
        setMessages((prev) => prev.map((message) => (
          message.id === loadingId
            ? { id: loadingId, role: 'assistant', error: execution.message || 'Le skill a échoué.' }
            : message
        )));
      } else {
        setMessages((prev) => prev.map((message) => (
          message.id === loadingId
            ? { id: loadingId, role: 'assistant', loading: true }
            : message
        )));
        await pollExecution(execution.id, loadingId);
      }
    } catch (error) {
      setMessages((prev) => prev.map((message) => (
        message.id === loadingId
          ? { id: loadingId, role: 'assistant', error: error.message || 'Impossible d’exécuter le skill.' }
          : message
      )));
    } finally {
      setSending(false);
    }
  }, [pollExecution, pushAssistantError]);

  const sendQuestion = useCallback(async (questionText) => {
    const trimmed = questionText.trim();
    if (!trimmed || sendingRef.current) {
      return;
    }

    const slash = parseSlash(trimmed);
    if (slash?.mode === 'exec' || (slash?.mode === 'menu' && slash.query)) {
      const slug = slash.mode === 'exec' ? slash.slug : slash.query;
      const skill = skills.find((item) => item.slug === slug);
      if (!skill) {
        pushAssistantError(trimmed, `Aucun skill « ${slug} ». Tapez / pour voir la liste.`);
        setInput('');
        return;
      }
      await runSkill(skill, slash.mode === 'exec' ? slash.rest : '');
      return;
    }

    const userMessage = {
      id: createMessageId(),
      role: 'user',
      content: trimmed,
    };
    const loadingId = createMessageId();

    setMessages((prev) => [
      ...prev,
      userMessage,
      { id: loadingId, role: 'assistant', loading: true },
    ]);
    setInput('');
    setSending(true);
    setPendingQuestion(trimmed);

    const effectiveMode = mode === ASSISTANT_MODES.synthese
      ? ASSISTANT_MODES.synthese
      : ASSISTANT_MODES.question;
    const top = effectiveMode === ASSISTANT_MODES.synthese ? 30 : 8;

    try {
      const result = await ragService.ask({
        question: trimmed,
        client: selectedClient || undefined,
        mode: effectiveMode,
        top,
      });

      setMessages((prev) => prev.map((message) => (
        message.id === loadingId
          ? {
              id: loadingId,
              role: 'assistant',
              content: result.answer,
              sources: result.sources || [],
            }
          : message
      )));
    } catch (error) {
      const errorMessage = error.name === 'AbortError'
        ? 'La requête a expiré. Veuillez réessayer.'
        : (error.message || 'Le service RAG est momentanément indisponible');

      setMessages((prev) => prev.map((message) => (
        message.id === loadingId
          ? { id: loadingId, role: 'assistant', error: errorMessage }
          : message
      )));
    } finally {
      setSending(false);
      setPendingQuestion(null);
    }
  }, [mode, selectedClient, skills, runSkill, pushAssistantError]);

  const handleRetry = useCallback(() => {
    if (pendingQuestion) {
      return;
    }
    const lastUserMessage = [...messages].reverse().find((message) => message.role === 'user');
    if (lastUserMessage) {
      setMessages((prev) => {
        let lastAssistantIndex = -1;
        for (let i = prev.length - 1; i >= 0; i -= 1) {
          if (prev[i].role === 'assistant') {
            lastAssistantIndex = i;
            break;
          }
        }
        if (lastAssistantIndex === -1) {
          return prev;
        }
        return prev.slice(0, lastAssistantIndex);
      });
      sendQuestion(lastUserMessage.content);
    }
  }, [messages, pendingQuestion, sendQuestion]);

  const selectSlashSkill = useCallback((skill) => {
    const texteParam = firstTexteParam(skill);
    if (texteParam) {
      setInput(`/${skill.slug} `);
      textareaRef.current?.focus();
      return;
    }
    runSkill(skill, '');
  }, [runSkill]);

  const handleComposerKeyDown = useCallback((event) => {
    if (slashOpen) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setSlashIndex((index) => Math.min(index + 1, slashItems.length - 1));
        return true;
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        setSlashIndex((index) => Math.max(index - 1, 0));
        return true;
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        setInput('');
        return true;
      }
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        const skill = slashItems[slashIndex];
        if (skill) {
          selectSlashSkill(skill);
        }
        return true;
      }
      if (event.key === 'Tab') {
        event.preventDefault();
        const skill = slashItems[slashIndex];
        if (skill) {
          setInput(`/${skill.slug} `);
        }
        return true;
      }
    }

    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      sendQuestion(input);
      return true;
    }

    return false;
  }, [slashOpen, slashItems, slashIndex, selectSlashSkill, sendQuestion, input]);

  return {
    clients,
    clientsLoading,
    clientsError,
    selectedClient,
    setSelectedClient: handleClientChange,
    syncFromPageMatch,
    mode,
    setMode,
    messages,
    input,
    setInput,
    sending,
    examples,
    syntheseDisabled,
    textareaRef,
    messagesEndRef,
    sendQuestion,
    handleRetry,
    resizeTextarea,
    slashOpen,
    slashItems,
    slashIndex,
    setSlashIndex,
    selectSlashSkill,
    handleComposerKeyDown,
  };
}
